'use client';

import { useCallback, useRef } from 'react';
import type { InteractionPlatformAdapters } from '../../model/interaction/adapters';
import { useAccurateTimer, type UseAccurateTimerOptions } from './useAccurateTimer';

export interface UseInteractionCoreOptions extends UseAccurateTimerOptions {
  /** Called once when timer completes */
  onComplete?: () => void;
}

export interface UseInteractionCoreReturn {
  /** Timer progress from 0 to 1 */
  progress: number;
  /** Seconds remaining on the countdown */
  remainingSeconds: number;
  /** Whether the timer has completed */
  isComplete: boolean;
  /** Whether timer is actively counting */
  isRunning: boolean;
  /** Pause the timer */
  pauseTimer: () => void;
  /** Resume the timer (if no pause locks active) */
  resumeTimer: () => void;
  /** Increment pause lock counter and pause timer */
  lockPause: () => void;
  /** Decrement pause lock counter (does NOT auto-resume) */
  unlockPause: () => void;
  /** Reset timer to initial state */
  resetTimer: () => void;
  /** Initialize the timer (load state from storage) */
  initialize: () => void;
}

/**
 * Headless hook that provides core interaction timer logic.
 * Platform-agnostic - works with any adapter implementation.
 *
 * This hook extracts the shared Provider logic so it can be reused
 * across web and mobile without duplicating code.
 *
 * @example
 * // In webapp Provider
 * const core = useInteractionCore(webAdapters, { onComplete });
 * // core.pauseTimer, core.resumeTimer, etc.
 *
 * @example
 * // In mobile Provider
 * const core = useInteractionCore(mobileAdapters, { onComplete });
 */
export function useInteractionCore(
  adapters: InteractionPlatformAdapters,
  options: UseInteractionCoreOptions = {},
): UseInteractionCoreReturn {
  const timer = useAccurateTimer(adapters, options);

  const pauseLockCountRef = useRef(0);

  const pauseTimer = useCallback(() => {
    timer.pause();
  }, [timer.pause]);

  const resumeTimer = useCallback(() => {
    if (pauseLockCountRef.current > 0) {
      return;
    }
    timer.resume();
  }, [timer.resume]);

  const lockPause = useCallback(() => {
    pauseLockCountRef.current += 1;
    timer.pause();
  }, [timer.pause]);

  /**
   * Decrements the pause lock counter.
   * NOTE: Does NOT automatically resume the timer.
   * Timer resume is handled by policy-based effects in the orchestrator.
   * This separation prevents conflicts between fraud locks and policy states.
   */
  const unlockPause = useCallback(() => {
    pauseLockCountRef.current = Math.max(0, pauseLockCountRef.current - 1);
  }, []);

  const resetTimer = useCallback(() => {
    timer.reset();
  }, [timer.reset]);

  return {
    progress: timer.progress,
    remainingSeconds: timer.remainingSeconds,
    isComplete: timer.isComplete,
    isRunning: timer.isRunning,
    pauseTimer,
    resumeTimer,
    lockPause,
    unlockPause,
    resetTimer,
    initialize: timer.initialize,
  };
}
